import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { FileText, Printer, Loader2 } from "lucide-react";

// UI Components
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader } from "components/ui/card";

// Store and Utils
import fetchRequest from "store/thunks/fetchRequest";
import { ParseTime } from "utils/parseTime";
import { printRequestDetails } from "./utils/printUtils";

// Components
import RequestForm from "./components/RequestForm";

interface RequestDetails {
  inquiry_type: string;
  inquiry_status: string;
  patient_name: string;
  inquiry_summary: string;
  inquiry_creation_ts: string;
  [key: string]: any;
}

interface RequestProps {
  requestDetails: RequestDetails | null;
  loading: boolean;
  error: string | null;
  fetchRequest: (requestId: string) => void;
  requestID: string;
}

const Request: React.FC<RequestProps> = ({
  requestDetails,
  loading,
  error,
  fetchRequest,
  requestID,
}) => {
  const [printData, setPrintData] = useState<any>(null);

  useEffect(() => {
    if (requestID) {
      fetchRequest(requestID);
    }
  }, [fetchRequest, requestID]);

  const handlePrintDataReady = (data: {
    tableResults: any;
    eligibilitySearchJson: any;
  }) => {
    setPrintData(data);
  };

  const handlePrint = () => {
    if (!requestDetails) return;

    // Use unified print function for all request types
    printRequestDetails({
      requestDetails,
      patientName: requestDetails.patient_name,
      subject: requestDetails.inquiry_summary,
      topic: requestDetails.inquiry_type,
      status: requestDetails.inquiry_status,
      time: ParseTime(requestDetails.inquiry_creation_ts),
      requestID: requestID,
      tableResults: printData?.tableResults,
      eligibilitySearchJson: printData?.eligibilitySearchJson,
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-[#005151]/10 rounded-full mb-4">
              <Loader2 className="h-8 w-8 animate-spin text-[#005151]" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Loading request details
            </h3>
            <p className="text-gray-600">
              Please wait while we fetch the request information...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="shadow-lg border-0 bg-red-50">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-red-100 rounded-full mb-4">
              <FileText className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-red-900 mb-2">
              Error loading request
            </h3>
            <p className="text-red-700">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!requestDetails) {
    return (
      <div className="p-6">
        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-gray-100 rounded-full mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No request selected
            </h3>
            <p className="text-gray-600">
              Please select a request to view its details.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Request Content */}
      <Card className="border-0 bg-white shadow-none">
        <CardHeader>
          {/* Hero Section with Print Button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-[#005151] rounded-xl">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Request Details -- {requestDetails.patient_name}
                </h1>
                <p className="text-gray-600 mt-1">View request information</p>
              </div>
            </div>
            <Button
              onClick={handlePrint}
              variant="outline"
              size="lg"
              className="border-[#005151] text-[#005151] hover:bg-[#005151] hover:text-white"
              disabled={!requestDetails}
            >
              <Printer className="h-5 w-5 mr-2" />
              Print
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <RequestForm
            requestDetails={requestDetails}
            patientName={requestDetails.patient_name}
            subject={requestDetails.inquiry_summary}
            topic={requestDetails.inquiry_type}
            status={requestDetails.inquiry_status}
            time={ParseTime(requestDetails.inquiry_creation_ts)}
            showRequestID={requestID}
            onPrintDataReady={handlePrintDataReady}
          />
        </CardContent>
      </Card>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  loading: state.fetchRequest.loading,
  requestDetails: state.fetchRequest.requestDetails,
  error: state.fetchRequest.requestError,
  requestID: state.fetchRequest.requestID,
});

const mapDispatchToProps = {
  fetchRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(Request);
